import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Alert, Grid, Container,
  Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip,
  CircularProgress, Avatar, Stack, LinearProgress, Autocomplete, InputAdornment,
  TablePagination, Switch
} from '@mui/material';
import {
  Business, VpnKey, Shield, History, Add, Refresh, CheckCircle,
  Error as ErrorIcon, Warning, Block, ContentCopy, Visibility,
  VisibilityOff, BarChart, Search, Upload, Download, Autorenew, People, Tune
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotifications } from '../contexts/NotificationContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const TIER_COLORS = {
  trial: '#9e9e9e',
  starter: '#2196f3',
  professional: '#ff9800',
  enterprise: '#4caf50'
};

const STATUS_COLORS = {
  active: 'success',
  expired: 'error',
  suspended: 'warning',
  revoked: 'default'
};

const TIER_INFO = {
  trial:        { label: 'Trial',        desc: '14 days · 2 frameworks · 1 account · 5 syncs/day' },
  starter:      { label: 'Starter',      desc: '5 frameworks · 3 accounts · 20 syncs/day' },
  professional: { label: 'Professional', desc: '15 frameworks · 10 accounts · 100 syncs/day' },
  enterprise:   { label: 'Enterprise',   desc: '100 frameworks · 50 accounts · 1000 syncs/day' }
};

function ControlPlaneAdmin() {
  const { showNotification } = useNotifications();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cpHealth, setCpHealth] = useState(null);

  // Data
  const [tenants, setTenants] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  // Search/filter
  const [tenantSearch, setTenantSearch] = useState('');
  const [licenseSearch, setLicenseSearch] = useState('');
  const [licenseStatusFilter, setLicenseStatusFilter] = useState('all');
  const [licenseTierFilter, setLicenseTierFilter] = useState('all');
  const [auditSearch, setAuditSearch] = useState('');

  // Pagination
  const [tenantPage, setTenantPage] = useState(0);
  const [licensePage, setLicensePage] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const rowsPerPage = 10;

  // Dialogs
  const [tenantDialog, setTenantDialog] = useState(false);
  const [licenseDialog, setLicenseDialog] = useState(false);
  const [usageDialog, setUsageDialog] = useState({ open: false, licenseKey: null });
  const [usageData, setUsageData] = useState(null);
  const [showLicenseKey, setShowLicenseKey] = useState({});

  // Form state
  const [newTenant, setNewTenant] = useState({ tenant_id: '', company_name: '', contact_email: '' });
  const [newLicense, setNewLicense] = useState({ tenant_id: '', tier: 'starter', expires_in_days: 365, allowed_frameworks: '' });
  const [selectedTenantOption, setSelectedTenantOption] = useState(null);

  // Framework dialogs
  const [frameworkDialog, setFrameworkDialog] = useState(false);
  const [checksDialog, setChecksDialog] = useState({ open: false, frameworkId: null });
  const [newFramework, setNewFramework] = useState({ framework_id: '', name: '', description: '', version: '1.0.0' });
  const [checksJson, setChecksJson] = useState('');
  const [checksVersion, setChecksVersion] = useState('1.0.0');
  const [frameworkSearch, setFrameworkSearch] = useState('');

  // Usage analytics
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState(30);

  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [userDialog, setUserDialog] = useState(false);
  const [featureDialog, setFeatureDialog] = useState({ open: false, user: null });
  const [newUser, setNewUser] = useState({ email: '', password: '', company_name: '', role: 'user' });
  const [editingFlags, setEditingFlags] = useState({});

  // CSPM Reports state
  const [cspmReports, setCspmReports] = useState([]);
  const [cspmReportsTotal, setCspmReportsTotal] = useState(0);
  const [cspmReportsPage, setCspmReportsPage] = useState(0);
  const [cspmReportsLoading, setCspmReportsLoading] = useState(false);
  const [cspmSearch, setCspmSearch] = useState('');
  const [cspmFindingsDialog, setCspmFindingsDialog] = useState({ open: false, scanId: null, scan: null });
  const [cspmFindings, setCspmFindings] = useState([]);
  const [cspmFindingsLoading, setCspmFindingsLoading] = useState(false);

  const FEATURES = [
    { key: 'cspm', label: 'CSPM', desc: 'Cloud Security Posture Management' },
    { key: 'finops', label: 'FinOps', desc: 'Cost Optimization & Resource Inventory' },
    { key: 'compliance', label: 'Compliance', desc: 'Compliance scanning & evidence' },
    { key: 'regulatory_bot', label: 'Astra Compliance', desc: 'Regulatory evidence bot' },
    { key: 'ai_assistant', label: 'Astra Cloud', desc: 'AI Cloud Assistant' },
    { key: 'support', label: 'Support', desc: 'Support ticket system' },
  ];

  const filteredUsers = useMemo(() => {
    if (!userSearch) return users;
    const q = userSearch.toLowerCase();
    return users.filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.company_name?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  // Filtered data
  const filteredTenants = useMemo(() => {
    if (!tenantSearch) return tenants;
    const q = tenantSearch.toLowerCase();
    return tenants.filter(t =>
      t.company_name?.toLowerCase().includes(q) ||
      t.tenant_id?.toLowerCase().includes(q) ||
      t.contact_email?.toLowerCase().includes(q)
    );
  }, [tenants, tenantSearch]);

  const filteredLicenses = useMemo(() => {
    let result = licenses;
    if (licenseSearch) {
      const q = licenseSearch.toLowerCase();
      result = result.filter(l =>
        l.company_name?.toLowerCase().includes(q) ||
        l.tenant_id?.toLowerCase().includes(q) ||
        l.license_key?.toLowerCase().includes(q) ||
        l.tier?.toLowerCase().includes(q)
      );
    }
    if (licenseStatusFilter !== 'all') {
      result = result.filter(l => {
        if (licenseStatusFilter === 'expired') return new Date(l.expires_at) < new Date();
        return l.status === licenseStatusFilter;
      });
    }
    if (licenseTierFilter !== 'all') {
      result = result.filter(l => l.tier === licenseTierFilter);
    }
    return result;
  }, [licenses, licenseSearch, licenseStatusFilter, licenseTierFilter]);

  const filteredAudit = useMemo(() => {
    if (!auditSearch) return auditLog;
    const q = auditSearch.toLowerCase();
    return auditLog.filter(e =>
      e.action?.toLowerCase().includes(q) ||
      e.entity_type?.toLowerCase().includes(q) ||
      e.entity_id?.toLowerCase().includes(q)
    );
  }, [auditLog, auditSearch]);

  // Tenant options for autocomplete
  const tenantOptions = useMemo(() =>
    tenants.filter(t => t.is_active).map(t => ({
      tenant_id: t.tenant_id,
      company_name: t.company_name,
      contact_email: t.contact_email || '',
      label: `${t.company_name} (${t.tenant_id.substring(0, 20)}...)`
    })),
    [tenants]
  );

  const filteredFrameworks = useMemo(() => {
    if (!frameworkSearch) return frameworks;
    const q = frameworkSearch.toLowerCase();
    return frameworks.filter(f =>
      f.name?.toLowerCase().includes(q) ||
      f.framework_id?.toLowerCase().includes(q) ||
      f.framework_key?.toLowerCase().includes(q)
    );
  }, [frameworks, frameworkSearch]);

  const checkHealth = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/health');
      setCpHealth(res.data);
    } catch {
      setCpHealth({ status: 'unreachable' });
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/tenants');
      setTenants(res.data.tenants || []);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  }, []);

  const loadLicenses = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/licenses');
      setLicenses(res.data.licenses || []);
    } catch (err) {
      console.error('Failed to load licenses:', err);
    }
  }, []);

  const loadFrameworks = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/frameworks');
      setFrameworks(res.data.frameworks || []);
    } catch (err) {
      console.error('Failed to load frameworks:', err);
    }
  }, []);

  const loadAuditLog = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/audit-log?limit=200');
      setAuditLog(res.data.entries || []);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get('/api/control-plane/users');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const loadUsageSummary = useCallback(async (days) => {    setUsageLoading(true);
    try {
      const res = await axios.get(`/api/control-plane/usage/summary?days=${days || usagePeriod}`);
      setUsageSummary(res.data);
    } catch (err) {
      console.error('Failed to load usage summary:', err);
    } finally {
      setUsageLoading(false);
    }
  }, [usagePeriod]);

  const loadCspmReports = useCallback(async (page = 0) => {
    setCspmReportsLoading(true);
    try {
      const res = await axios.get(`/api/control-plane/cspm-reports?limit=10&offset=${page * 10}`);
      setCspmReports(res.data.reports || []);
      setCspmReportsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load CSPM reports:', err);
    } finally {
      setCspmReportsLoading(false);
    }
  }, []);

  const loadCspmFindings = useCallback(async (scanId) => {
    setCspmFindingsLoading(true);
    try {
      const res = await axios.get(`/api/control-plane/cspm-reports/${scanId}/findings`);
      setCspmFindings(res.data.findings || []);
      setCspmFindingsDialog(prev => ({ ...prev, scan: res.data.scan || null }));
    } catch (err) {
      console.error('Failed to load CSPM findings:', err);
    } finally {
      setCspmFindingsLoading(false);
    }
  }, []);

  const filteredCspmReports = useMemo(() => {
    if (!cspmSearch) return cspmReports;
    const q = cspmSearch.toLowerCase();
    return cspmReports.filter(r =>
      r.account_name?.toLowerCase().includes(q) ||
      r.aws_account_id?.toLowerCase().includes(q) ||
      r.company_name?.toLowerCase().includes(q)
    );
  }, [cspmReports, cspmSearch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([checkHealth(), loadTenants(), loadLicenses(), loadFrameworks(), loadAuditLog(), loadUsageSummary(), loadUsers(), loadCspmReports()]);
    } catch {
      setError('Failed to load control plane data');
    } finally {
      setLoading(false);
    }
  }, [checkHealth, loadTenants, loadLicenses, loadFrameworks, loadAuditLog, loadUsageSummary, loadUsers, loadCspmReports]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreateTenant = async () => {
    if (!newTenant.tenant_id || !newTenant.company_name) {
      showNotification('Tenant ID and company name are required', 'warning');
      return;
    }
    try {
      await axios.post('/api/control-plane/tenants', newTenant);
      showNotification('Tenant created', 'success');
      setTenantDialog(false);
      setNewTenant({ tenant_id: '', company_name: '', contact_email: '' });
      loadTenants();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to create tenant', 'error');
    }
  };

  const handleIssueLicense = async () => {
    const tenantId = selectedTenantOption?.tenant_id || newLicense.tenant_id;
    if (!tenantId || !newLicense.tier) {
      showNotification('Tenant and tier are required', 'warning');
      return;
    }
    try {
      const payload = {
        tenant_id: tenantId,
        tier: newLicense.tier,
        expires_in_days: parseInt(newLicense.expires_in_days) || 365,
        allowed_frameworks: newLicense.allowed_frameworks
          ? newLicense.allowed_frameworks.split(',').map(f => f.trim()).filter(Boolean)
          : []
      };
      const res = await axios.post('/api/control-plane/licenses/issue', payload);
      showNotification(`License issued: ${res.data.license?.license_key?.substring(0, 16)}...`, 'success');
      setLicenseDialog(false);
      setNewLicense({ tenant_id: '', tier: 'starter', expires_in_days: 365, allowed_frameworks: '' });
      setSelectedTenantOption(null);
      loadLicenses();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to issue license', 'error');
    }
  };

  const handleUpdateLicenseStatus = async (licenseKey, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this license?`)) return;
    try {
      await axios.put(`/api/control-plane/licenses/${encodeURIComponent(licenseKey)}/status`, { status });
      showNotification(`License ${status}`, 'success');
      loadLicenses();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to update license', 'error');
    }
  };

  const handleRenewLicense = async (licenseKey, tier) => {
    const days = tier === 'trial' ? 14 : 365;
    if (!window.confirm(`Renew this license for ${days} days from today?`)) return;
    try {
      await axios.put(`/api/control-plane/licenses/${encodeURIComponent(licenseKey)}/renew`, { extends_days: days });
      showNotification(`License renewed for ${days} days`, 'success');
      loadLicenses();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to renew license', 'error');
    }
  };

  const handleViewUsage = async (licenseKey) => {
    setUsageDialog({ open: true, licenseKey });
    setUsageData(null);
    try {
      const res = await axios.get(`/api/control-plane/licenses/${encodeURIComponent(licenseKey)}/usage?days=30`);
      setUsageData(res.data);
    } catch (err) {
      console.error('Failed to load usage:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('Copied to clipboard', 'success');
  };

  const handleToggleTenant = async (tenantId, currentActive) => {
    try {
      await axios.put(`/api/control-plane/tenants/${encodeURIComponent(tenantId)}/toggle`, { is_active: !currentActive });
      showNotification(`Tenant ${!currentActive ? 'enabled' : 'disabled'}`, 'success');
      loadTenants();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to toggle tenant', 'error');
    }
  };

  const handleToggleFramework = async (frameworkId, currentActive) => {
    try {
      await axios.put(`/api/control-plane/frameworks/${encodeURIComponent(frameworkId)}/toggle`, { is_active: !currentActive });
      showNotification(`Framework ${!currentActive ? 'enabled' : 'disabled'}`, 'success');
      loadFrameworks();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to toggle framework', 'error');
    }
  };

  const handleCreateFramework = async () => {
    if (!newFramework.framework_id || !newFramework.name) {
      showNotification('Framework ID and name are required', 'warning');
      return;
    }
    try {
      await axios.post('/api/control-plane/frameworks', newFramework);
      showNotification('Framework created', 'success');
      setFrameworkDialog(false);
      setNewFramework({ framework_id: '', name: '', description: '', version: '1.0.0' });
      loadFrameworks();
      loadAuditLog();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to create framework', 'error');
    }
  };

  const handleUploadChecks = async () => {
    if (!checksJson.trim()) {
      showNotification('Please paste the checks JSON', 'warning');
      return;
    }
    try {
      const parsed = JSON.parse(checksJson);
      const checks = Array.isArray(parsed) ? parsed : parsed.checks || [];
      if (checks.length === 0) {
        showNotification('No checks found in JSON', 'warning');
        return;
      }
      await axios.put(`/api/control-plane/frameworks/${encodeURIComponent(checksDialog.frameworkId)}/checks`, {
        version: checksVersion,
        checks
      });
      showNotification(`Uploaded ${checks.length} checks`, 'success');
      setChecksDialog({ open: false, frameworkId: null });
      setChecksJson('');
      setChecksVersion('1.0.0');
      loadFrameworks();
      loadAuditLog();
    } catch (err) {
      if (err instanceof SyntaxError) {
        showNotification('Invalid JSON format', 'error');
      } else {
        showNotification(err.response?.data?.error || 'Failed to upload checks', 'error');
      }
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      showNotification('Email and password are required', 'warning');
      return;
    }
    try {
      await axios.post('/api/control-plane/users', newUser);
      showNotification('User created successfully', 'success');
      setUserDialog(false);
      setNewUser({ email: '', password: '', company_name: '', role: 'user' });
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  const handleOpenFeatureDialog = (user) => {
    const defaults = { cspm: true, finops: true, compliance: false, support: false, ai_assistant: false, regulatory_bot: false };
    setEditingFlags({ ...defaults, ...(user.feature_flags || {}) });
    setFeatureDialog({ open: true, user });
  };

  const handleSaveFeatureFlags = async () => {
    try {
      await axios.put(`/api/control-plane/users/${featureDialog.user.id}/features`, { feature_flags: editingFlags });
      showNotification('Feature access updated', 'success');
      setFeatureDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to update features', 'error');
    }
  };

  const handleToggleUser = async (userId, currentActive) => {
    try {
      await axios.put(`/api/control-plane/users/${userId}/toggle`, { is_active: !currentActive });
      showNotification(`User ${!currentActive ? 'enabled' : 'disabled'}`, 'success');
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to toggle user', 'error');
    }
  };

  const handleDownloadChecks = async (frameworkId, frameworkName) => {    try {
      const res = await axios.get(`/api/control-plane/frameworks/${encodeURIComponent(frameworkId)}/checks`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${frameworkId}-checks.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showNotification(`Downloaded checks for ${frameworkName || frameworkId}`, 'success');
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to download checks', 'error');
    }
  };

  // Search bar component
  const SearchBar = ({ value, onChange, placeholder }) => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>
      }}
      sx={{ minWidth: 260 }}
    />
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#1565c0', width: 56, height: 56 }}>
              <Shield sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>Control Plane Admin</Typography>
              <Typography variant="body1" color="text.secondary">
                Manage tenants, licenses, frameworks, and audit logs
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              size="small"
              icon={cpHealth?.status === 'healthy' ? <CheckCircle /> : <ErrorIcon />}
              label={cpHealth?.status === 'healthy' ? 'Control Plane Online' : 'Control Plane Offline'}
              color={cpHealth?.status === 'healthy' ? 'success' : 'error'}
              variant="outlined"
            />
            <Button startIcon={<Refresh />} onClick={loadAll} disabled={loading} variant="outlined" size="small">
              Refresh
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1565c0' }}>{tenants.length}</Typography>
                <Typography variant="body2" color="text.secondary">Tenants</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {licenses.filter(l => l.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Active Licenses</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                  {frameworks.filter(f => f.is_active).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Frameworks</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{users.length}</Typography>
                <Typography variant="body2" color="text.secondary">Users</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab icon={<Business />} label="Tenants" iconPosition="start" />
            <Tab icon={<VpnKey />} label="Licenses" iconPosition="start" />
            <Tab icon={<Shield />} label="Frameworks" iconPosition="start" />
            <Tab icon={<People />} label="Users" iconPosition="start" />
            <Tab icon={<History />} label="Audit Log" iconPosition="start" />
            <Tab icon={<BarChart />} label="Usage" iconPosition="start" />
            <Tab icon={<Shield />} label="CSPM Reports" iconPosition="start" />
          </Tabs>

          <CardContent>

            {/* TENANTS TAB */}
            <TabPanel value={tab} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">Tenants ({filteredTenants.length})</Typography>
                  <SearchBar value={tenantSearch} onChange={(v) => { setTenantSearch(v); setTenantPage(0); }} placeholder="Search by name, ID, or email..." />
                </Box>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setTenantDialog(true)}>
                    New Tenant
                  </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Tenant ID</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Licenses</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTenants.slice(tenantPage * rowsPerPage, tenantPage * rowsPerPage + rowsPerPage).map((t) => (
                      <TableRow key={t.tenant_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.company_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t.tenant_id}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {t.tenant_id.length > 28 ? t.tenant_id.substring(0, 28) + '...' : t.tenant_id}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{t.contact_email || '-'}</TableCell>
                        <TableCell>{t.license_count || 0}</TableCell>
                        <TableCell>
                          <Chip size="small" label={t.is_active ? 'Active' : 'Inactive'}
                            color={t.is_active ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell>{formatDate(t.created_at)}</TableCell>
                        <TableCell>
                          <Tooltip title={t.is_active ? 'Disable tenant' : 'Enable tenant'}>
                            <Switch size="small" checked={t.is_active} onChange={() => handleToggleTenant(t.tenant_id, t.is_active)} />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredTenants.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        {tenantSearch ? 'No tenants match your search' : 'No tenants found'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredTenants.length > rowsPerPage && (
                <TablePagination
                  component="div" count={filteredTenants.length} page={tenantPage}
                  onPageChange={(_, p) => setTenantPage(p)} rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]} />
              )}
            </TabPanel>

            {/* LICENSES TAB */}
            <TabPanel value={tab} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">Licenses ({filteredLicenses.length})</Typography>
                  <SearchBar value={licenseSearch} onChange={(v) => { setLicenseSearch(v); setLicensePage(0); }} placeholder="Search by company, key, or tenant..." />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={licenseStatusFilter} label="Status" onChange={(e) => { setLicenseStatusFilter(e.target.value); setLicensePage(0); }}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="revoked">Revoked</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Tier</InputLabel>
                    <Select value={licenseTierFilter} label="Tier" onChange={(e) => { setLicenseTierFilter(e.target.value); setLicensePage(0); }}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="trial">Trial</MenuItem>
                      <MenuItem value="starter">Starter</MenuItem>
                      <MenuItem value="professional">Professional</MenuItem>
                      <MenuItem value="enterprise">Enterprise</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setLicenseDialog(true)}>
                  Issue License
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>License Key</TableCell>
                      <TableCell>Tenant</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Limits</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLicenses.slice(licensePage * rowsPerPage, licensePage * rowsPerPage + rowsPerPage).map((l) => {
                      const keyVisible = showLicenseKey[l.license_key];
                      const displayKey = keyVisible ? l.license_key : l.license_key.substring(0, 12) + '...';
                      return (
                        <TableRow key={l.license_key} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <code style={{ fontSize: '0.8rem' }}>{displayKey}</code>
                              <IconButton size="small" onClick={() => setShowLicenseKey(prev => ({ ...prev, [l.license_key]: !prev[l.license_key] }))}>
                                {keyVisible ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                              </IconButton>
                              <IconButton size="small" onClick={() => copyToClipboard(l.license_key)}>
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{l.company_name || '-'}</Typography>
                            <Tooltip title={l.tenant_id}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {l.tenant_id?.substring(0, 20)}...
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={l.tier.toUpperCase()}
                              sx={{ bgcolor: TIER_COLORS[l.tier] || '#9e9e9e', color: '#fff', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={l.status} color={STATUS_COLORS[l.status] || 'default'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">{l.max_frameworks} fw / {l.max_aws_accounts} accts</Typography>
                            <Typography variant="caption" display="block" color="text.secondary">{l.max_syncs_per_day} syncs/day / {l.rate_limit_rpm} rpm</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(l.expires_at)}</Typography>
                            {new Date(l.expires_at) < new Date() && (
                              <Chip size="small" label="EXPIRED" color="error" sx={{ mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Usage">
                                <IconButton size="small" onClick={() => handleViewUsage(l.license_key)}>
                                  <BarChart sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              {(l.status === 'expired' || new Date(l.expires_at) < new Date() || l.status === 'suspended') && (
                                <Tooltip title="Renew License">
                                  <IconButton size="small" color="primary" onClick={() => handleRenewLicense(l.license_key, l.tier)}>
                                    <Autorenew sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {l.status === 'active' && new Date(l.expires_at) >= new Date() && (
                                <Tooltip title="Suspend">
                                  <IconButton size="small" color="warning" onClick={() => handleUpdateLicenseStatus(l.license_key, 'suspended')}>
                                    <Warning sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {l.status === 'suspended' && (
                                <Tooltip title="Reactivate">
                                  <IconButton size="small" color="success" onClick={() => handleUpdateLicenseStatus(l.license_key, 'active')}>
                                    <CheckCircle sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {l.status !== 'revoked' && (
                                <Tooltip title="Revoke">
                                  <IconButton size="small" color="error" onClick={() => handleUpdateLicenseStatus(l.license_key, 'revoked')}>
                                    <Block sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredLicenses.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        {licenseSearch || licenseStatusFilter !== 'all' || licenseTierFilter !== 'all'
                          ? 'No licenses match your filters' : 'No licenses found'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredLicenses.length > rowsPerPage && (
                <TablePagination
                  component="div" count={filteredLicenses.length} page={licensePage}
                  onPageChange={(_, p) => setLicensePage(p)} rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]} />
              )}
            </TabPanel>

            {/* FRAMEWORKS TAB */}
            <TabPanel value={tab} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">Frameworks ({filteredFrameworks.filter(f => f.is_active).length} active / {filteredFrameworks.length} total)</Typography>
                  <SearchBar value={frameworkSearch} onChange={setFrameworkSearch} placeholder="Search by name or key..." />
                </Box>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setFrameworkDialog(true)}>
                  Add Framework
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Key</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Checks</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFrameworks.map((f) => {
                      const fwId = f.framework_id || f.framework_key;
                      return (
                        <TableRow key={fwId} hover sx={{ opacity: f.is_active ? 1 : 0.6 }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.name}</Typography>
                            {f.description && (
                              <Typography variant="caption" color="text.secondary">{f.description}</Typography>
                            )}
                          </TableCell>
                          <TableCell><code style={{ fontSize: '0.8rem' }}>{fwId}</code></TableCell>
                          <TableCell>{f.version || '-'}</TableCell>
                          <TableCell>{f.total_checks || f.total_controls || 0}</TableCell>
                          <TableCell>
                            <Chip size="small" label={f.is_active ? 'Active' : 'Inactive'}
                              color={f.is_active ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={f.is_active ? 'Disable' : 'Enable'}>
                                <Switch size="small" checked={f.is_active} onChange={() => handleToggleFramework(fwId, f.is_active)} />
                              </Tooltip>
                              <Tooltip title="Upload Checks">
                                <IconButton size="small" onClick={() => { setChecksDialog({ open: true, frameworkId: fwId }); setChecksVersion(f.version || '1.0.0'); }}>
                                  <Upload sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Checks">
                                <IconButton size="small" onClick={() => handleDownloadChecks(fwId, f.name)}>
                                  <Download sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredFrameworks.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        {frameworkSearch ? 'No frameworks match your search' : 'No frameworks found'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* USERS TAB */}
            <TabPanel value={tab} index={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">Users ({filteredUsers.length})</Typography>
                  <SearchBar value={userSearch} onChange={(v) => { setUserSearch(v); setUserPage(0); }} placeholder="Search by email, company..." />
                </Box>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setUserDialog(true)}>
                  Add User
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                CSPM and FinOps features are enabled by default for all users. Other features must be explicitly enabled per user.
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>License</TableCell>
                      <TableCell>Features Enabled</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.slice(userPage * rowsPerPage, userPage * rowsPerPage + rowsPerPage).map((u) => {
                      const flags = u.feature_flags || {};
                      const enabledCount = FEATURES.filter(f => flags[f.key] !== false).length;
                      return (
                        <TableRow key={u.id} hover sx={{ opacity: u.is_active === false ? 0.55 : 1 }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.email}</Typography>
                          </TableCell>
                          <TableCell>{u.company_name || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={u.role} color={u.role === 'admin' ? 'primary' : 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {u.tier ? (
                              <Chip size="small" label={u.tier.toUpperCase()}
                                sx={{ bgcolor: TIER_COLORS[u.tier] || '#9e9e9e', color: '#fff', fontWeight: 600 }} />
                            ) : <Typography variant="caption" color="text.secondary">None</Typography>}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {FEATURES.map(f => (
                                <Chip
                                  key={f.key}
                                  size="small"
                                  label={f.label}
                                  color={flags[f.key] !== false ? 'success' : 'default'}
                                  variant={flags[f.key] !== false ? 'filled' : 'outlined'}
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={u.is_active === false ? 'Inactive' : 'Active'}
                              color={u.is_active === false ? 'default' : 'success'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(u.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Manage feature access">
                                <IconButton size="small" color="primary" onClick={() => handleOpenFeatureDialog(u)}>
                                  <Tune sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={u.is_active === false ? 'Enable user' : 'Disable user'}>
                                <Switch size="small" checked={u.is_active !== false}
                                  onChange={() => handleToggleUser(u.id, u.is_active !== false)} />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        {userSearch ? 'No users match your search' : 'No users found'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredUsers.length > rowsPerPage && (
                <TablePagination
                  component="div" count={filteredUsers.length} page={userPage}
                  onPageChange={(_, p) => setUserPage(p)} rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]} />
              )}
            </TabPanel>

            {/* AUDIT LOG TAB */}
            <TabPanel value={tab} index={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h6">Audit Log ({filteredAudit.length})</Typography>
                <SearchBar value={auditSearch} onChange={(v) => { setAuditSearch(v); setAuditPage(0); }} placeholder="Search by action, entity..." />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Entity</TableCell>
                      <TableCell>Entity ID</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAudit.slice(auditPage * rowsPerPage, auditPage * rowsPerPage + rowsPerPage).map((e, i) => (
                      <TableRow key={e.id || i} hover>
                        <TableCell>
                          <Typography variant="caption">{formatDateTime(e.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={e.action} variant="outlined"
                            color={e.action?.includes('create') ? 'success' : e.action?.includes('revoke') ? 'error' : 'default'} />
                        </TableCell>
                        <TableCell>{e.entity_type || '-'}</TableCell>
                        <TableCell>
                          <Tooltip title={e.entity_id || ''}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {e.entity_id ? (e.entity_id.length > 24 ? e.entity_id.substring(0, 24) + '...' : e.entity_id) : '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 300, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {e.details ? (typeof e.details === 'string' ? e.details : JSON.stringify(e.details)) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAudit.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        {auditSearch ? 'No entries match your search' : 'No audit entries found'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredAudit.length > rowsPerPage && (
                <TablePagination
                  component="div" count={filteredAudit.length} page={auditPage}
                  onPageChange={(_, p) => setAuditPage(p)} rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]} />
              )}
            </TabPanel>

            {/* USAGE & ANALYTICS TAB */}
            <TabPanel value={tab} index={5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">Usage & Analytics</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Period</InputLabel>
                    <Select value={usagePeriod} label="Period" onChange={(e) => { setUsagePeriod(e.target.value); loadUsageSummary(e.target.value); }}>
                      <MenuItem value={7}>Last 7 days</MenuItem>
                      <MenuItem value={30}>Last 30 days</MenuItem>
                      <MenuItem value={60}>Last 60 days</MenuItem>
                      <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={() => loadUsageSummary()}><Refresh /></IconButton>
                </Box>
              </Box>

              {usageLoading && <LinearProgress sx={{ mb: 2 }} />}

              {usageSummary && (
                <>
                  {/* Overview Stats */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { label: 'Total Users', value: usageSummary.overview?.total_users || 0, color: '#2A6BDB' },
                      { label: 'Active Users', value: usageSummary.overview?.active_users || 0, color: '#4CAF50' },
                      { label: 'AWS Accounts', value: usageSummary.overview?.aws_accounts || 0, color: '#1E4FA0' },
                      { label: 'Security Scans', value: usageSummary.overview?.security_scans || 0, color: '#3D7BE0' },
                      { label: 'Compliance Scans', value: usageSummary.overview?.compliance_scans || 0, color: '#5B9AFF' },
                      { label: 'Evidence Generations', value: usageSummary.overview?.evidence_generations || 0, color: '#7b1fa2' },
                      { label: 'AI Sessions', value: usageSummary.overview?.ai_sessions || 0, color: '#f57c00' },
                      { label: 'AI Messages', value: usageSummary.overview?.ai_messages || 0, color: '#ff9800' },
                      { label: 'Cost Analyses', value: usageSummary.overview?.cost_analyses || 0, color: '#2e7d32' },
                      { label: 'Savings Identified', value: `$${Number(usageSummary.overview?.total_savings_identified || 0).toLocaleString()}`, color: '#d32f2f' },
                    ].map((stat, i) => (
                      <Grid item xs={6} sm={4} md={2.4} key={i}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: '8px' }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={3}>
                    {/* Framework Adoption */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Framework Adoption (by scan count)</Typography>
                        {(usageSummary.framework_adoption || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No compliance scans yet</Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Framework</TableCell>
                                <TableCell align="right">Scans</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(usageSummary.framework_adoption || []).map((f, i) => (
                                <TableRow key={i} hover>
                                  <TableCell><code style={{ fontSize: '0.8rem' }}>{f.framework_id}</code></TableCell>
                                  <TableCell align="right"><Chip size="small" label={f.scan_count} color="primary" variant="outlined" /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Paper>
                    </Grid>

                    {/* Security Scan Trend */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Security Scan Trend (daily)</Typography>
                        {(usageSummary.scan_trend || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No security scans yet</Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Scans</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(usageSummary.scan_trend || []).slice(0, 10).map((d, i) => (
                                <TableRow key={i} hover>
                                  <TableCell>{new Date(d.date).toLocaleDateString()}</TableCell>
                                  <TableCell align="right">{d.count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Paper>
                    </Grid>

                    {/* Recent Users */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Recent Users</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Email</TableCell>
                              <TableCell>Company</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell>Joined</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(usageSummary.recent_users || []).map((u, i) => (
                              <TableRow key={i} hover>
                                <TableCell><Typography variant="caption">{u.email}</Typography></TableCell>
                                <TableCell>{u.company_name || '-'}</TableCell>
                                <TableCell><Chip size="small" label={u.role} variant="outlined" /></TableCell>
                                <TableCell><Typography variant="caption">{formatDate(u.created_at)}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>

                    {/* Support Tickets */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Support Tickets</Typography>
                        {Object.keys(usageSummary.support_tickets || {}).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No support tickets</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {Object.entries(usageSummary.support_tickets || {}).map(([status, count]) => (
                              <Paper key={status} variant="outlined" sx={{ p: 1.5, textAlign: 'center', minWidth: 80, borderRadius: '8px' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{count}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{status}</Typography>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}

              {!usageLoading && !usageSummary && (
                <Alert severity="info">Click Refresh to load usage analytics</Alert>
              )}
            </TabPanel>

            {/* CSPM REPORTS TAB */}
            <TabPanel value={tab} index={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">CSPM Scan Reports ({cspmReportsTotal})</Typography>
                  <TextField size="small" placeholder="Search by account, company..." value={cspmSearch}
                    onChange={(e) => setCspmSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                    sx={{ minWidth: 260 }} />
                </Box>
                <Button startIcon={<Refresh />} onClick={() => loadCspmReports(cspmReportsPage)} disabled={cspmReportsLoading}>
                  Refresh
                </Button>
              </Box>

              {cspmReportsLoading && <LinearProgress sx={{ mb: 2 }} />}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>AWS Account</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell align="center">Passed</TableCell>
                      <TableCell align="center">Failed</TableCell>
                      <TableCell>Scan Date</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCspmReports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {cspmReportsLoading ? 'Loading...' : 'No CSPM scan reports found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredCspmReports.map((report) => (
                      <TableRow key={report.scan_id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{report.company_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{report.account_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{report.aws_account_id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={report.status}
                            size="small"
                            color={report.status === 'completed' ? 'success' : report.status === 'running' ? 'info' : report.status === 'failed' ? 'error' : 'default'}
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{
                            fontWeight: 700,
                            color: report.risk_score >= 80 ? 'success.main' : report.risk_score >= 60 ? 'warning.main' : 'error.main'
                          }}>
                            {report.risk_score ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>{report.passed ?? 0}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>{report.failed ?? 0}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {report.started_at ? new Date(report.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {report.duration_ms ? `${Math.round(report.duration_ms / 1000)}s` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {report.status === 'completed' && (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <Tooltip title="View Findings">
                                <IconButton size="small" onClick={() => {
                                  setCspmFindingsDialog({ open: true, scanId: report.scan_id, scan: report });
                                  loadCspmFindings(report.scan_id);
                                }}>
                                  <Visibility sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download XLSX Report">
                                <IconButton size="small" onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = `/api/control-plane/cspm-reports/${report.scan_id}/download`;
                                  link.setAttribute('download', '');
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}>
                                  <Download sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {cspmReportsTotal > 10 && (
                <TablePagination component="div" count={cspmReportsTotal} page={cspmReportsPage}
                  onPageChange={(_, p) => { setCspmReportsPage(p); loadCspmReports(p); }}
                  rowsPerPage={10} rowsPerPageOptions={[]} />
              )}
            </TabPanel>

          </CardContent>
        </Card>

        {/* CREATE TENANT DIALOG */}
        <Dialog open={tenantDialog} onClose={() => setTenantDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Tenant ID" fullWidth size="small" value={newTenant.tenant_id}
                onChange={(e) => setNewTenant({ ...newTenant, tenant_id: e.target.value })}
                helperText="Unique identifier (e.g. tenant_abc123)" />
              <TextField label="Company Name" fullWidth size="small" required value={newTenant.company_name}
                onChange={(e) => setNewTenant({ ...newTenant, company_name: e.target.value })} />
              <TextField label="Contact Email" fullWidth size="small" type="email" value={newTenant.contact_email}
                onChange={(e) => setNewTenant({ ...newTenant, contact_email: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTenantDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateTenant}>Create</Button>
          </DialogActions>
        </Dialog>

        {/* ISSUE LICENSE DIALOG */}
        <Dialog open={licenseDialog} onClose={() => { setLicenseDialog(false); setSelectedTenantOption(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Issue New License</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Autocomplete
                options={tenantOptions}
                value={selectedTenantOption}
                onChange={(_, val) => {
                  setSelectedTenantOption(val);
                  if (val) setNewLicense(prev => ({ ...prev, tenant_id: val.tenant_id }));
                }}
                getOptionLabel={(opt) => opt.label || ''}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.tenant_id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.company_name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {opt.tenant_id.substring(0, 32)}...
                      </Typography>
                      {opt.contact_email && (
                        <Typography variant="caption" display="block" color="text.secondary">{opt.contact_email}</Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Search Tenant" size="small" placeholder="Type to search by name or ID..." required />
                )}
                isOptionEqualToValue={(opt, val) => opt.tenant_id === val.tenant_id}
                noOptionsText="No active tenants found"
                fullWidth
              />
              <FormControl fullWidth size="small">
                <InputLabel>Tier</InputLabel>
                <Select value={newLicense.tier} label="Tier" onChange={(e) => setNewLicense({ ...newLicense, tier: e.target.value })}>
                  {Object.entries(TIER_INFO).map(([key, info]) => (
                    <MenuItem key={key} value={key}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{info.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{info.desc}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Expires In (days)" fullWidth size="small" type="number" value={newLicense.expires_in_days}
                onChange={(e) => setNewLicense({ ...newLicense, expires_in_days: e.target.value })} />
              <TextField label="Allowed Frameworks (comma-separated, leave empty for all)" fullWidth size="small"
                value={newLicense.allowed_frameworks} placeholder="e.g. soc2, hipaa, pci-dss"
                onChange={(e) => setNewLicense({ ...newLicense, allowed_frameworks: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setLicenseDialog(false); setSelectedTenantOption(null); }}>Cancel</Button>
            <Button variant="contained" onClick={handleIssueLicense}>Issue License</Button>
          </DialogActions>
        </Dialog>

        {/* USAGE DIALOG */}
        <Dialog open={usageDialog.open} onClose={() => setUsageDialog({ open: false, licenseKey: null })} maxWidth="sm" fullWidth>

          <DialogTitle>License Usage (Last 30 Days)</DialogTitle>
          <DialogContent>
            {!usageData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Summary</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Total Requests:</Typography>
                  <Typography variant="body2">{usageData.summary?.total_requests || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Unique Frameworks:</Typography>
                  <Typography variant="body2">{usageData.summary?.unique_frameworks || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Last Used:</Typography>
                  <Typography variant="body2">{formatDateTime(usageData.summary?.last_used)}</Typography>
                </Box>
                {usageData.daily_usage?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Daily Breakdown</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Requests</TableCell>
                            <TableCell>Frameworks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {usageData.daily_usage.slice(0, 10).map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{formatDate(d.date)}</TableCell>
                              <TableCell>{d.request_count}</TableCell>
                              <TableCell>{d.frameworks_accessed}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUsageDialog({ open: false, licenseKey: null })}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ADD FRAMEWORK DIALOG */}
        <Dialog open={frameworkDialog} onClose={() => setFrameworkDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Framework</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Framework ID" fullWidth size="small" required value={newFramework.framework_id}
                onChange={(e) => setNewFramework({ ...newFramework, framework_id: e.target.value })}
                helperText="Unique key (e.g. soc2, hipaa, pci-dss)" />
              <TextField label="Name" fullWidth size="small" required value={newFramework.name}
                onChange={(e) => setNewFramework({ ...newFramework, name: e.target.value })} />
              <TextField label="Description" fullWidth size="small" multiline rows={2} value={newFramework.description}
                onChange={(e) => setNewFramework({ ...newFramework, description: e.target.value })} />
              <TextField label="Version" fullWidth size="small" value={newFramework.version}
                onChange={(e) => setNewFramework({ ...newFramework, version: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFrameworkDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateFramework}>Create</Button>
          </DialogActions>
        </Dialog>

        {/* UPLOAD CHECKS DIALOG */}
        <Dialog open={checksDialog.open} onClose={() => setChecksDialog({ open: false, frameworkId: null })} maxWidth="md" fullWidth>
          <DialogTitle>Upload Checks — {checksDialog.frameworkId}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info" variant="outlined">
                Paste a JSON array of checks. Each check needs: check_id, title. Optional: description, severity (CRITICAL/HIGH/MEDIUM/LOW), category, aws_service, requirement_section, check_function, official_reference.
              </Alert>
              <TextField label="Version" size="small" value={checksVersion}
                onChange={(e) => setChecksVersion(e.target.value)} sx={{ maxWidth: 200 }} />
              <TextField
                label="Checks JSON"
                fullWidth
                multiline
                rows={12}
                value={checksJson}
                onChange={(e) => setChecksJson(e.target.value)}
                placeholder={'[\n  {\n    "check_id": "soc2-cc6.1",\n    "title": "Logical Access Controls",\n    "severity": "HIGH",\n    "category": "Access Control"\n  }\n]'}
                sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChecksDialog({ open: false, frameworkId: null })}>Cancel</Button>
            <Button variant="contained" startIcon={<Upload />} onClick={handleUploadChecks}>Upload Checks</Button>
          </DialogActions>
        </Dialog>

        {/* ADD USER DIALOG */}
        <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Email" fullWidth size="small" type="email" required
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
              <TextField
                label="Password" fullWidth size="small" type="password" required
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                helperText="Minimum 8 characters"
              />
              <TextField
                label="Company Name" fullWidth size="small"
                value={newUser.company_name}
                onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select value={newUser.role} label="Role" onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateUser}>Create User</Button>
          </DialogActions>
        </Dialog>

        {/* FEATURE FLAGS DIALOG */}
        <Dialog open={featureDialog.open} onClose={() => setFeatureDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            Manage Feature Access
            {featureDialog.user && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {featureDialog.user.email} · {featureDialog.user.company_name || 'No company'}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              CSPM and FinOps are enabled by default. Toggle other features as needed.
            </Typography>
            <Stack spacing={0} sx={{ mt: 1 }}>
              {FEATURES.map((f) => (
                <Box
                  key={f.key}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{f.desc}</Typography>
                  </Box>
                  <Switch
                    checked={editingFlags[f.key] !== false}
                    onChange={(e) => setEditingFlags(prev => ({ ...prev, [f.key]: e.target.checked }))}
                    color="primary"
                  />
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFeatureDialog({ open: false, user: null })}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveFeatureFlags}>Save Changes</Button>
          </DialogActions>
        </Dialog>

        {/* CSPM FINDINGS DIALOG */}
        <Dialog open={cspmFindingsDialog.open} onClose={() => { setCspmFindingsDialog({ open: false, scanId: null, scan: null }); setCspmFindings([]); }} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" component="span">CSPM Scan Findings</Typography>
                {cspmFindingsDialog.scan && (
                  <Typography variant="body2" color="text.secondary">
                    {cspmFindingsDialog.scan.company_name} — {cspmFindingsDialog.scan.account_name} ({cspmFindingsDialog.scan.aws_account_id})
                    {cspmFindingsDialog.scan.risk_score != null && (
                      <Chip label={`Score: ${cspmFindingsDialog.scan.risk_score}`} size="small" sx={{ ml: 1, fontWeight: 700,
                        bgcolor: cspmFindingsDialog.scan.risk_score >= 80 ? 'success.main' : cspmFindingsDialog.scan.risk_score >= 60 ? 'warning.main' : 'error.main',
                        color: 'white' }} />
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {cspmFindingsLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : cspmFindings.length === 0 ? (
              <Alert severity="info">No findings for this scan</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Severity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Check</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell>Resource</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cspmFindings.map((f, i) => (
                      <TableRow key={f.id || i} hover>
                        <TableCell>
                          <Chip label={f.severity} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem',
                            bgcolor: f.severity === 'CRITICAL' ? '#b71c1c' : f.severity === 'HIGH' ? '#d32f2f' : f.severity === 'MEDIUM' ? '#ed6c02' : f.severity === 'LOW' ? '#0288d1' : '#4caf50',
                            color: 'white' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={f.status} size="small" variant="outlined"
                            color={f.status === 'PASSED' ? 'success' : f.status === 'FAILED' ? 'error' : 'default'}
                            sx={{ fontWeight: 600, fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 300 }}>{f.check_title || '—'}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{f.service || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{f.region || 'global'}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', maxWidth: 200, display: 'block' }}>
                            {f.resource_arn || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCspmFindingsDialog({ open: false, scanId: null, scan: null }); setCspmFindings([]); }}>Close</Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
}

export default ControlPlaneAdmin;
